import PropertyGrid from "@/components/property/PropertyGrid";
import PropertyResultsHeader from "@/components/property/PropertyResultsHeader";
import { Button } from "@/components/ui/button";
import { useFeaturedProperties } from "@/hooks/useFeaturedProperties";
import {
  useProvinces,
  useAllDistricts,
  useAllCities,
} from "@/hooks/useLocations";
import { usePropertyTypes } from "@/hooks/usePropertyTypes";
import { useListings } from "@/lib/api/listings";
import {
  Listing,
  ListingFilters,
  ListingsResponse,
  ListingType,
  Province, // Import Province
  District, // Import District
  City, // Import City
} from "@/lib/types";
import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ListingsResultsSummary } from "../listings/ListingsResultsSummary";
import FeaturedPropertiesSection from "../search/FeaturedPropertiesSection";
import { Pagination } from "../ui/pagination";
import ActiveFiltersDisplay from "../search/ActiveFiltersDisplay";

interface PropertyTypeItem {
  name: string;
  display_name?: string;
  [key: string]: any;
}

interface PropertyContentProps {
  filters?: ListingFilters;
  onClearFilters: () => void;
  commercialProperties?: Listing[];
}

const getCategoryDisplayName = (categoryValue: string | undefined): string => {
  switch (categoryValue) {
    case "sale":
      return "For Sale";
    case "rent":
      return "For Rent";
    case "lease":
      return "For Lease";
    default:
      return "";
  }
};

// Helper to convert comma-separated string to array or undefined
const stringToArray = (str: string | null | undefined): string[] => {
  return str
    ? str
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
};

// Helper to get display name and type (province, district, city) from slug
const getLocationTypeAndName = (
  slug: string,
  provinces: Province[],
  districts: District[],
  cities: City[]
) => {
  const province = provinces.find((p) => p.slug === slug);
  if (province)
    return { type: "province", name: province.name, id: province.id };
  const district = districts.find((d) => d.slug === slug);
  if (district)
    return {
      type: "district",
      name: district.name,
      id: district.id,
      province_id: district.province_id,
    };
  const city = cities.find((c) => c.slug === slug);
  if (city)
    return {
      type: "city",
      name: city.name,
      id: city.id,
      district_id: city.district_id,
    };
  return null;
};

const PropertyContent = ({
  filters: customFilters,
  onClearFilters,
  commercialProperties = [],
}: PropertyContentProps) => {
  const [displayMode, setDisplayMode] = useState<"grid" | "list">("grid");
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortBy, setSortBy] = useState<string>("created_at");

  const { propertyTypes = [] } = usePropertyTypes();
  const { data: allProvincesData } = useProvinces();
  const { data: allDistrictsData } = useAllDistricts();
  const { data: allCitiesData } = useAllCities();

  const propertyTypeMap = useMemo(() => {
    const map = new Map<string, string>();
    (propertyTypes as PropertyTypeItem[]).forEach((type) => {
      map.set(type.name, type.display_name || type.name);
    });
    return map;
  }, [propertyTypes]);

  // Create maps for looking up display names from IDs
  const provinceMap = useMemo(() => {
    const map = new Map<number, string>();
    (allProvincesData || []).forEach((p) => {
      map.set(p.id, p.name);
    });
    return map;
  }, [allProvincesData]);

  const districtMap = useMemo(() => {
    const map = new Map<number, string>();
    (allDistrictsData || []).forEach((d) => {
      map.set(d.id, d.name);
    });
    return map;
  }, [allDistrictsData]);

  const cityMap = useMemo(() => {
    const map = new Map<number, string>();
    (allCitiesData || []).forEach((c) => {
      map.set(c.id, c.display_name || c.name);
    });
    return map;
  }, [allCitiesData]);

  const filters = useMemo(() => {
    if (customFilters) {
      return customFilters;
    }

    const params = Object.fromEntries(searchParams.entries());
    const derivedFilters: ListingFilters = {
      page: Number(params.page) || 1,
      limit: Number(params.limit) || 12,
      search_term: params.search_term?.trim() || undefined,
      location: stringToArray(params.location), // Use unified 'location'
      propertyType: params.propertyType
        ? stringToArray(params.propertyType.toString())
        : undefined,
      listing_type: params.listing_type
        ? (stringToArray(params.listing_type.toString()) as ListingType[])
        : undefined,
      minPrice: params.minPrice ? Number(params.minPrice) : undefined,
      maxPrice: params.maxPrice ? Number(params.maxPrice) : undefined,
      sort_by: params.sort_by || undefined,
      sort_order: params.sort_order as "asc" | "desc" | undefined,
      bedrooms: params.bedrooms ? Number(params.bedrooms) : undefined,
      bathrooms: params.bathrooms ? Number(params.bathrooms) : undefined,
      featured: params.featured === "true" || undefined,
      boosted: params.boosted === "true" || undefined,
      status:
        (params.status as "draft" | "published" | "archived" | undefined) ||
        "published",
    };
    Object.keys(derivedFilters).forEach((key) => {
      if (derivedFilters[key as keyof ListingFilters] === undefined) {
        delete derivedFilters[key as keyof ListingFilters];
      }
    });

    return derivedFilters;
  }, [searchParams, customFilters]);

  const { data, isLoading, isError, error } = useListings(filters, {
    keepPreviousData: true,
  });

  const typedData = data as ListingsResponse | undefined;

  const defaultPagination = {
    totalItems: 0,
    totalPages: 1,
    currentPage: filters.page || 1,
    itemsPerPage: filters.limit || 12,
  };

  const properties: Listing[] =
    typedData?.properties.map((p) => ({
      ...p,
      listing: p,
      featured: p.is_featured === 1,
      boosted: p.is_boosted === 1,
    })) || [];
  const pagination = typedData?.pagination || defaultPagination;

  const { data: featuredPropertiesData, isLoading: isFeaturedLoading } =
    useFeaturedProperties(filters);
  const featuredProperties = featuredPropertiesData || [];

  // Derive active location slugs from filters.location for ActiveFiltersDisplay
  const derivedActiveLocations = useMemo(() => {
    let province: string | null = null;
    let district: string | null = null;
    let city: string | null = null;

    for (const slug of filters.location || []) {
      const locInfo = getLocationTypeAndName(
        slug,
        allProvincesData || [],
        allDistrictsData || [],
        allCitiesData || []
      );
      if (locInfo) {
        if (locInfo.type === "province") {
          province = slug;
        } else if (locInfo.type === "district") {
          district = slug;
        } else if (locInfo.type === "city") {
          city = slug;
        }
      }
    }
    return { province, district, city };
  }, [filters.location, allProvincesData, allDistrictsData, allCitiesData]);

  const activePropertyTypes = filters.propertyType || [];
  const activeListingTypes = filters.listing_type || [];
  const activeProvinceSlug = derivedActiveLocations.province;
  const activeDistrictSlug = derivedActiveLocations.district;
  const activeCitySlug = derivedActiveLocations.city;

  const handleRemoveFilter = useCallback(
    (
      type:
        | "propertyType"
        | "listingType"
        | "province"
        | "district"
        | "city"
        | "priceRange"
        | "sqftRange"
        | "featured",
      valueToRemove?: string | number
    ) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", "1"); // Always reset page on filter change

      switch (type) {
        case "propertyType":
          if (typeof valueToRemove === "string") {
            const currentValues = stringToArray(newParams.get("propertyType"));
            const newValues = currentValues.filter((v) => v !== valueToRemove);
            if (newValues.length > 0) {
              newParams.set("propertyType", newValues.join(","));
            } else {
              newParams.delete("propertyType");
            }
          }
          break;
        case "listingType":
          if (typeof valueToRemove === "string") {
            const currentValues = stringToArray(newParams.get("listing_type"));
            const newValues = currentValues.filter((v) => v !== valueToRemove);
            if (newValues.length > 0) {
              newParams.set("listing_type", newValues.join(","));
            } else {
              newParams.delete("listing_type");
            }
          }
          break;
        case "province":
          // Remove province and all its children from 'location' param
          const provinceToRemove = allProvincesData?.find(
            (p) => p.slug === valueToRemove
          );
          if (provinceToRemove) {
            const districtsInProvince = allDistrictsData
              ?.filter((d) => d.province_id === provinceToRemove.id)
              .map((d) => d.slug);
            const citiesInProvince = allCitiesData
              ?.filter((c) =>
                districtsInProvince?.includes(
                  allDistrictsData?.find((d) => d.id === c.district_id)?.slug ||
                    ""
                )
              )
              .map((c) => c.slug);

            const currentLocations = stringToArray(newParams.get("location"));
            const updatedLocations = currentLocations.filter(
              (slug) =>
                slug !== valueToRemove &&
                !districtsInProvince?.includes(slug) &&
                !citiesInProvince?.includes(slug)
            );
            if (updatedLocations.length > 0) {
              newParams.set("location", updatedLocations.join(","));
            } else {
              newParams.delete("location");
            }
          }
          break;
        case "district":
          // Remove district and its children cities from 'location' param
          const districtToRemove = allDistrictsData?.find(
            (d) => d.slug === valueToRemove
          );
          if (districtToRemove) {
            const citiesInDistrict = allCitiesData
              ?.filter((c) => c.district_id === districtToRemove.id)
              .map((c) => c.slug);

            const currentLocations = stringToArray(newParams.get("location"));
            const updatedLocations = currentLocations.filter(
              (slug) =>
                slug !== valueToRemove && !citiesInDistrict?.includes(slug)
            );
            if (updatedLocations.length > 0) {
              newParams.set("location", updatedLocations.join(","));
            } else {
              newParams.delete("location");
            }
          }
          break;
        case "city":
          // Remove only the city from 'location' param
          const currentLocations = stringToArray(newParams.get("location"));
          const updatedLocations = currentLocations.filter(
            (slug) => slug !== valueToRemove
          );
          if (updatedLocations.length > 0) {
            newParams.set("location", updatedLocations.join(","));
          } else {
            newParams.delete("location");
          }
          break;
        case "priceRange":
          newParams.delete("minPrice");
          newParams.delete("maxPrice");
          break;
        case "sqftRange":
          newParams.delete("minSize");
          newParams.delete("maxSize");
          break;
        case "featured":
          newParams.delete("featured");
          break;
        default:
          break;
      }
      setSearchParams(newParams, { replace: true });
    },
    [
      searchParams,
      setSearchParams,
      allProvincesData,
      allDistrictsData,
      allCitiesData,
    ]
  );

  const handleSortChange = useCallback(
    (sortValue: string) => {
      setSortBy(sortValue);

      const newParams = new URLSearchParams(searchParams);

      switch (sortValue) {
        case "latest":
          newParams.set("sort_by", "created_at");
          newParams.set("sort_order", "desc");
          break;
        case "oldest":
          newParams.set("sort_by", "created_at");
          newParams.set("sort_order", "asc");
          break;
        case "price_high":
          newParams.set("sort_by", "price");
          newParams.set("sort_order", "desc");
          break;
        case "price_low":
          newParams.set("sort_by", "price");
          newParams.set("sort_order", "asc");
          break;
        case "name_asc":
          newParams.set("sort_by", "title");
          newParams.set("sort_order", "asc");
          break;
        case "name_desc":
          newParams.set("sort_by", "title");
          newParams.set("sort_order", "desc");
          break;
        default:
          newParams.set("sort_by", "created_at");
          newParams.set("sort_order", "desc");
      }

      newParams.set("page", "1");
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const getPropertyBoostedStatus = (id: string | number) => {
    const property = properties.find((p) => p.id === id || p.wp_id === id);
    return property?.is_boosted === 1 || false;
  };

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", page.toString());
    setSearchParams(newParams, { replace: true });
  };

  const priceRangeProps = useMemo(
    () => ({
      min: filters.minPrice || 0,
      max: filters.maxPrice || 50000000,
    }),
    [filters.minPrice, filters.maxPrice]
  );

  const sqftRangeProps = useMemo(
    () => ({
      min: filters.minSize || 0,
      max: filters.maxSize || 10000,
    }),
    [filters.minSize, filters.maxSize]
  );

  return (
    <div className="flex-grow">
      <div className="sticky top-0 z-20 bg-background pt-2 pb-1 mb-4">
        {" "}
        {/* Removed border-b */}
        <PropertyResultsHeader
          count={pagination.totalItems}
          searchQuery={filters?.search_term || null}
          displayMode={displayMode}
          setDisplayMode={setDisplayMode}
          onSortChange={handleSortChange}
          sortBy={sortBy}
        />
        <ListingsResultsSummary
          currentPage={pagination.currentPage}
          itemsPerPage={pagination.itemsPerPage}
          totalItems={pagination.totalItems}
          isLoading={isLoading}
        />
      </div>

      <ActiveFiltersDisplay
        propertyTypes={activePropertyTypes.filter((type) => type !== "")}
        listingTypes={activeListingTypes
          .flatMap((type) => type.split(",").map((s) => s.trim()))
          .filter((type) => type !== "")}
        provinceSlug={activeProvinceSlug}
        districtSlug={activeDistrictSlug}
        citySlug={activeCitySlug}
        priceRange={priceRangeProps}
        sqftRange={sqftRangeProps}
        featured={filters.featured}
        onRemoveFilter={handleRemoveFilter}
        onClearAll={onClearFilters}
      />

      {featuredProperties.length > 0 && !isFeaturedLoading && (
        <FeaturedPropertiesSection
          featuredProperties={featuredProperties}
          isLoading={isFeaturedLoading}
          displayMode={displayMode}
          handleClearFilters={onClearFilters}
          getPropertyBoostedStatus={getPropertyBoostedStatus}
        />
      )}

      <PropertyGrid
        properties={properties}
        isLoading={isLoading}
        displayMode={displayMode}
        onClearFilters={onClearFilters}
      />

      {pagination.totalPages > 1 && (
        <div className="mt-8">
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  );
};

export default PropertyContent;
